import { onBeforeUnmount, onUnmounted, watch, type Ref } from "vue";

import {
  notifyScrollInEditor,
  notifyScrollInPreview,
  resetScrollSyncStore,
  scrollSyncStore,
} from "../stores/scroll-sync";

export const useEditorScrollSync = (
  scrollRef: Ref<HTMLElement | undefined>,
  enabled: Ref<boolean>
) => {
  let detachEditorScrollListener: (() => void) | undefined;

  const updateScrollProgressFromEditor = () => {
    const el = scrollRef.value;
    if (!el) return;
    const maxScroll = el.scrollHeight - el.clientHeight;
    notifyScrollInEditor(maxScroll > 0 ? el.scrollTop / maxScroll : 0);
  };

  const applyScrollProgressFromPreview = (progress: number) => {
    const el = scrollRef.value;
    if (!el) return;
    const maxScroll = el.scrollHeight - el.clientHeight;
    const targetTop = progress * maxScroll;
    el.scrollTop = targetTop;
  };

  watch(
    [scrollRef],
    ([el]) => {
      detachEditorScrollListener?.();
      if (!el) return;

      const handler = () => updateScrollProgressFromEditor();
      el.addEventListener("scroll", handler, { passive: true });
      detachEditorScrollListener = () => {
        el.removeEventListener("scroll", handler);
        detachEditorScrollListener = undefined;
      };

      requestAnimationFrame(updateScrollProgressFromEditor);
    },
    { immediate: true }
  );

  watch(
    scrollSyncStore,
    (state) => {
      if (state.source !== "preview" || !enabled.value) return;
      applyScrollProgressFromPreview(state.progress);
    },
    { immediate: true }
  );

  watch(enabled, (next) => {
    if (next) updateScrollProgressFromEditor();
  });

  onBeforeUnmount(() => {
    detachEditorScrollListener?.();
    resetScrollSyncStore();
  });
};

export const usePreviewScrollSync = (
  iframeRef: Ref<HTMLIFrameElement | null | undefined>,
  enabled: Ref<boolean>
) => {
  const uid = crypto.randomUUID();
  let detachPreviewScrollListener: (() => void) | undefined;

  const applyProgressFromEditor = (progress: number) => {
    const iframe = iframeRef.value;
    const scrollEl = iframe?.contentDocument?.scrollingElement;
    if (!iframe || !scrollEl) return;

    const maxScroll = scrollEl.scrollHeight - iframe.clientHeight;
    const targetTop = progress * maxScroll;

    scrollEl.scrollTop = targetTop;
  };

  const attachPreviewScrollListener = (
    contentWindow: Window,
    contentDocument: Document
  ) => {
    detachPreviewScrollListener?.();

    const scrollEl = contentDocument.scrollingElement;
    if (!scrollEl) return;

    const onScroll = () => {
      const iframe = iframeRef.value;
      if (!iframe) return;

      const maxScroll = scrollEl.scrollHeight - iframe.clientHeight;
      const currentTop = scrollEl.scrollTop;
      const progress = maxScroll > 0 ? currentTop / maxScroll : 0;

      notifyScrollInPreview(progress);
    };

    contentWindow.addEventListener("scroll", onScroll);
    detachPreviewScrollListener = () => {
      contentWindow.removeEventListener("scroll", onScroll);
      detachPreviewScrollListener = undefined;
    };
  };

  watch(
    scrollSyncStore,
    (state) => {
      if (state.source !== "editor" || !enabled.value) return;
      applyProgressFromEditor(state.progress);
    },
    { immediate: true }
  );

  const attachPreviewIfReady = () => {
    const iframe = iframeRef.value;
    const contentWindow = iframe?.contentWindow;
    const contentDocument = iframe?.contentDocument;
    if (!contentWindow || !contentDocument) return;
    attachPreviewScrollListener(contentWindow, contentDocument);
  };

  const handleMessage = (ev: MessageEvent) => {
    if (ev.data !== uid) return;
    attachPreviewIfReady();
    iframeRef.value?.contentWindow?.addEventListener(
      "load",
      attachPreviewIfReady,
      { once: true }
    );
  };
  window.addEventListener("message", handleMessage);
  onUnmounted(() => {
    window.removeEventListener("message", handleMessage);
  });

  watch(enabled, (next) => {
    if (next) applyProgressFromEditor(scrollSyncStore.value.progress);
  });

  watch(iframeRef, () => {
    attachPreviewIfReady();
  });

  onUnmounted(() => {
    detachPreviewScrollListener?.();
  });

  return { uid };
};
